import type { IWindow } from '../Window';

export const PENDING_EXPOSURES_KEY = 'fi_pending_exposures';

type Exposures = Record<string, string>;

const segment = () => (window as IWindow).analytics as (IWindow['analytics'] & { initialized?: boolean }) | undefined;

function readStored(): Exposures {
  try {
    return JSON.parse(sessionStorage.getItem(PENDING_EXPOSURES_KEY) ?? '{}');
  } catch {
    return {};
  }
}

function sendExposure(name: string, variant: string) {
  segment()?.track('User Viewed Experiment', { name, variant });
}

let pending: Exposures = {};

/**
 * Until analytics.js loads, a track call waits in the snippet's in-page queue, and a full page load
 * drops it. Main's pages leave by full page load, the landing split's pdp arm often before
 * analytics.js has arrived, so the exposures this page queued are kept for the next page unless
 * analytics.js took them.
 */
function keepUnsentExposures() {
  try {
    if (segment()?.initialized || Object.keys(pending).length === 0) {
      sessionStorage.removeItem(PENDING_EXPOSURES_KEY);
    } else {
      sessionStorage.setItem(PENDING_EXPOSURES_KEY, JSON.stringify(pending));
    }
  } catch {
    // storage blocked: the exposure is lost, as it was before
  }
}

export function trackExposure(name: string, variant: string) {
  sendExposure(name, variant);
  pending[name] = variant;
}

/**
 * Sends the exposures an earlier page queued but left before analytics.js took them, and returns
 * them so this page doesn't track them again. Call once per page.
 */
export function resumePendingExposures(): Exposures {
  pending = readStored();
  Object.entries(pending).forEach(([name, variant]) => sendExposure(name, variant));
  window.addEventListener('pagehide', keepUnsentExposures);
  return { ...pending };
}
